import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
    MdSearch,
    MdFileDownload,
    MdTrendingUp,
    MdArrowBack,
    MdFilterList,
    MdDescription,
    MdCurrencyRupee
} from 'react-icons/md';
import styles from './ProjectPOList.module.css';
import Loader from '../components/common/Loader';
import Pagination from '../components/common/Pagination';
import NoData from '../components/common/NoData';
import Drawer from '../components/common/Drawer';
import Swal from 'sweetalert2';
import PaginatedSelect from '../components/common/PaginatedSelect';
import { authFetch } from '../components/utils/authFetch';
import { GET_PROJECT_PO_LIST_REPORT_API, GENERATE_PO_PDF_API, BASE_API, GET_ALL_VENDORS_API } from '../config/API';
import { toast } from 'react-toastify';
import axios from 'axios';

const ProjectPOList = () => {
    const { projectId } = useParams();
    const navigate = useNavigate();

    const [loading, setLoading] = useState(false);
    const [reports, setReports] = useState([]);
    const [projectDetails, setProjectDetails] = useState(null);
    const [summary, setSummary] = useState(null);

    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [totalRecords, setTotalRecords] = useState(0);
    const [searchTerm, setSearchTerm] = useState('');
    const [debouncedSearch, setDebouncedSearch] = useState('');
    const [showFilters, setShowFilters] = useState(false);
    const [dateRange, setDateRange] = useState({ start: '', end: '' });
    const [selectedVendor, setSelectedVendor] = useState(null);

    useEffect(() => {
        const timer = setTimeout(() => {
            setDebouncedSearch(searchTerm);
        }, 500);
        return () => clearTimeout(timer);
    }, [searchTerm]);

    const fetchReports = async () => {
        setLoading(true);
        try {
            const token = localStorage.getItem('accessToken');
            let queryParams = `?page=${currentPage}&limit=10&search=${debouncedSearch}`;

            if (dateRange.start) queryParams += `&startDate=${dateRange.start}`;
            if (dateRange.end) queryParams += `&endDate=${dateRange.end}`;
            if (selectedVendor?.value) queryParams += `&vendorId=${selectedVendor.value}`;

            const response = await authFetch(
                `${GET_PROJECT_PO_LIST_REPORT_API(projectId)}${queryParams}`,
                {
                    headers: {
                        Authorization: `Bearer ${token}`,
                    },
                }
            );
            const result = await response.json();

            if (result.status) {
                setReports(result.data || []);
                setProjectDetails(result.projectDetails);
                setSummary(result.summary);

                const pagination = result.pagination;
                setTotalPages(pagination?.totalPages || 1);
                setTotalRecords(pagination?.totalCount || result.data?.length || 0);
            } else {
                setReports([]);
                setSummary(null);
                setTotalPages(1);
                setTotalRecords(0);
            }
        } catch (error) {
            console.error('Error fetching project PO list:', error);
            setReports([]);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchReports();
    }, [projectId, currentPage, debouncedSearch, dateRange, selectedVendor]);

    const formatCurrency = (amount) => {
        const val = parseFloat(amount || 0);
        return `₹${val.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
    };

    const handleGeneratePDF = async (poId) => {
        Swal.fire({
            title: 'Generating PDF...',
            text: 'Please wait, generating your Purchase Order PDF.',
            allowOutsideClick: false,
            didOpen: () => {
                Swal.showLoading();
            }
        });

        try {
            const token = localStorage.getItem('accessToken');
            const response = await axios.get(GENERATE_PO_PDF_API(poId), {
                headers: { Authorization: `Bearer ${token}` }
            });

            if (response.data.status && response.data.pdf) {
                const fullPdfUrl = `${BASE_API}/${response.data.pdf}`;
                const fileResponse = await axios.get(fullPdfUrl, {
                    headers: { Authorization: `Bearer ${token}` },
                    responseType: 'blob'
                });

                const fileURL = URL.createObjectURL(new Blob([fileResponse.data]));
                const link = document.createElement('a');
                link.href = fileURL;
                link.setAttribute('download', `PO_${poId}.pdf`);
                document.body.appendChild(link);
                link.click();
                link.parentNode.removeChild(link);
                URL.revokeObjectURL(fileURL);

                Swal.close();
            } else {
                throw new Error(response.data.message || 'Failed to generate PDF URL');
            }
        } catch (error) {
            console.error('Error generating PDF:', error);
            Swal.fire('Error', 'Failed to generate PDF. Please try again.', 'error');
        }
    };

    const handleExportCSV = async () => {
        try {
            const token = localStorage.getItem('accessToken');
            let queryParams = `?exportCSV=true&search=${debouncedSearch}`;

            if (dateRange.start) queryParams += `&startDate=${dateRange.start}`;
            if (dateRange.end) queryParams += `&endDate=${dateRange.end}`;
            if (selectedVendor?.value) queryParams += `&vendorId=${selectedVendor.value}`;

            const response = await authFetch(`${GET_PROJECT_PO_LIST_REPORT_API(projectId)}${queryParams}`, {
                headers: {
                    Authorization: `Bearer ${token}`,
                },
            });

            const result = await response.json();

            if (result.status) {
                toast.success(result.message || 'Export started! Check Report Log for the file.');
            } else {
                toast.error(result.message || 'Failed to start export');
            }
        } catch (error) {
            console.error('Error exporting CSV:', error);
            toast.error('An error occurred during export');
        }
    };

    const getStatusClass = (status) => {
        const s = status?.toLowerCase();
        if (s === 'pending') return styles['status-pending'];
        if (s === 'completed' || s === 'active') return styles['status-completed'];
        if (s === 'cancelled') return styles['status-cancelled'];
        return '';
    };

    return (
        <div className={styles['purchase-orders-container']}>
            {/* Header Section */}
            <div className={styles['purchase-orders-header']}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                    <button className="back-btn" onClick={() => navigate(-1)}>
                        <MdArrowBack size={20} />
                    </button>
                    <div>
                        <h2>PO List • {projectDetails?.name || 'Project'}</h2>
                        <p style={{ margin: 0, fontSize: '13px', color: '#6b7280' }}>
                            {projectDetails?.projectCode}
                        </p>
                    </div>
                </div>
                <div className={styles['header-actions']}>
                    <button
                        className={`${styles['filter-btn']} ${showFilters ? styles.active : ''}`}
                        onClick={() => setShowFilters(!showFilters)}
                    >
                        <MdFilterList /> Filter
                    </button>
                    <button
                        className={styles['export-btn']}
                        onClick={handleExportCSV}
                    >
                        <MdFileDownload size={18} /> Export CSV
                    </button>
                </div>
            </div>

            <Drawer
                isOpen={showFilters}
                onClose={() => setShowFilters(false)}
                title="Filter Reports"
            >
                <div className={styles['drawer-filter-content']}>
                    <div className={styles['filter-item']}>
                        <label>Vendor</label>
                        <PaginatedSelect
                            apiUrl={GET_ALL_VENDORS_API}
                            mapResponse={(v) => ({
                                value: v.id,
                                label: `${v.vendorName} (${v.companyName})`
                            })}
                            value={selectedVendor}
                            onChange={(option) => setSelectedVendor(option)}
                            placeholder="Select Vendor"
                            isClearable
                        />
                    </div>
                    <div className={styles['filter-item']}>
                        <label>Start Date</label>
                        <input
                            type="date"
                            value={dateRange.start}
                            onChange={(e) => setDateRange({ ...dateRange, start: e.target.value })}
                        />
                    </div>
                    <div className={styles['filter-item']}>
                        <label>End Date</label>
                        <input
                            type="date"
                            value={dateRange.end}
                            onChange={(e) => setDateRange({ ...dateRange, end: e.target.value })}
                        />
                    </div>
                    <div className={styles['drawer-actions']}>
                        <button
                            className={styles['clear-filters-btn']}
                            onClick={() => {
                                setDateRange({ start: '', end: '' });
                                setSelectedVendor(null);
                            }}
                            style={{ width: '100%' }}
                        >
                            Reset Filter
                        </button>
                    </div>
                </div>
            </Drawer>

            {/* Stats Grid */}
            <div className={styles['stats-grid']}>
                <div className={styles['stat-card']}>
                    <div className={styles['stat-header']}>
                        <div className={`${styles['icon-box']} ${styles['purple']}`}>
                            <MdDescription />
                        </div>
                    </div>
                    <span className={styles['stat-label']}>Total POs</span>
                    <span className={styles['stat-value']}>{summary?.totalPOs || 0}</span>
                </div>
                <div className={styles['stat-card']}>
                    <div className={styles['stat-header']}>
                        <div className={`${styles['icon-box']} ${styles['blue']}`}>
                            <MdCurrencyRupee />
                        </div>
                    </div>
                    <span className={styles['stat-label']}>Committed Amount</span>
                    <span className={styles['stat-value']}>{formatCurrency(summary?.totalAmount)}</span>
                </div>
                <div className={styles['stat-card']}>
                    <div className={styles['stat-header']}>
                        <div className={`${styles['icon-box']} ${styles['green']}`}>
                            <MdCurrencyRupee />
                        </div>
                    </div>
                    <span className={styles['stat-label']}>Paid Amount</span>
                    <span className={`${styles['stat-value']} ${styles['amount-paid']}`}>{formatCurrency(summary?.totalPaidAmount)}</span>
                </div>
                <div className={styles['stat-card']}>
                    <div className={styles['stat-header']}>
                        <div className={`${styles['icon-box']} ${styles['red']}`}>
                            <MdCurrencyRupee />
                        </div>
                    </div>
                    <span className={styles['stat-label']}>Remaining</span>
                    <span className={`${styles['stat-value']} ${styles['amount-remaining']}`}>{formatCurrency(summary?.totalRemainingAmount)}</span>
                </div>
            </div>

            {/* Main Content Table Card */}
            <div className={styles['card-container-po']}>
                <div className={styles['search-container']}>
                    <div className={styles['search-wrapper']}>
                        <MdSearch className={styles['search-icon']} />
                        <input
                            type="text"
                            placeholder="Search PO number, vendor or items..."
                            className={styles['search-input']}
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                        />
                    </div>
                </div>

                <div className={styles['table-container']}>
                    <table>
                        <thead>
                            <tr>
                                <th>PO Number</th>
                                <th>Vendor Details</th>
                                <th>Date</th>
                                <th className={styles['text-center']}>Item Details</th>
                                <th>Status</th>
                                <th className={styles['text-right']}>Total Amount</th>
                                <th className={styles['text-right']}>Paid Amount</th>
                                <th className={styles['text-right']}>Remaining</th>
                                <th className={styles['text-center']}>Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {loading ? (
                                <tr>
                                    <td colSpan="9" style={{ textAlign: 'center', padding: '3rem' }}>
                                        <Loader text="Fetching PO list..." />
                                    </td>
                                </tr>
                            ) : reports.length === 0 ? (
                                <tr>
                                    <td colSpan="9" style={{ textAlign: 'center', padding: '3rem' }}>
                                        <NoData title="No Purchase Orders" message="No POs found for this project." />
                                    </td>
                                </tr>
                            ) : (
                                reports.map((po, index) => (
                                    <tr key={po.poId || index}>
                                        <td className={styles['po-number-cell']}>
                                            <span
                                                className={styles['link-text']}
                                                onClick={() => window.open(`/reports/po/details/${po.poId}`, '_blank')}
                                            >
                                                {po.poNumber}
                                            </span>
                                        </td>
                                        <td>
                                            <div className={styles['vendor-info']}>
                                                <div className={styles['vendor-name']}>{po.vendorName}</div>
                                                <div className={styles['vendor-meta']}>{po.companyName} • {po.vendorCategory}</div>
                                            </div>
                                        </td>
                                        <td>{po.poDate}</td>
                                        <td className={styles['text-center']}>
                                            <div className={styles['items-cell-container']}>
                                                <span
                                                    className={styles['link-text']}
                                                    onClick={() => window.open(`/reports/po/details/${po.poId}`, '_blank')}
                                                >
                                                    {po.totalProducts || 0} Items
                                                </span>
                                                {po.products?.length > 0 && (
                                                    <div className={styles['items-tooltip']}>
                                                        {po.products.map((prod, i) => (
                                                            <span key={i} className={styles['item-badge']}>
                                                                {prod.productName}
                                                            </span>
                                                        ))}
                                                    </div>
                                                )}
                                            </div>
                                        </td>
                                        <td>
                                            <span className={`${styles['status-badge']} ${getStatusClass(po.poStatus)}`}>
                                                {po.poStatus}
                                            </span>
                                        </td>
                                        <td className={`${styles['amount-cell-grandTotal']} ${styles['text-right']}`}>{formatCurrency(po.totalAmount)}</td>
                                        <td className={`${styles['amount-cell-paid']} ${styles['text-right']}`}>{formatCurrency(po.totalPaidAmount)}</td>
                                        <td className={`${styles['amount-cell-remaining']} ${styles['text-right']}`}>{formatCurrency(po.totalRemainingAmount)}</td>
                                        <td className={styles['text-center']}>
                                            <button
                                                className={styles['action-btn']}
                                                onClick={() => handleGeneratePDF(po.poId)}
                                                title="Download PO PDF"
                                            >
                                                <MdFileDownload size={20} />
                                            </button>
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>

                <Pagination
                    currentPage={currentPage}
                    totalPages={totalPages}
                    totalRecords={totalRecords}
                    onPageChange={(page) => setCurrentPage(page)}
                    currentCount={reports.length}
                    entityName="Purchase Order"
                />
            </div>
        </div>
    );
};

export default ProjectPOList;
